import type { Leg, TimeData, WaitEvent } from '../core/TimeData';
import {
  type CargoEvent,
  CoreEventType,
  getEventLocationId,
  isCargoEventTimeData,
  isCoaEvent,
  isDischargeEvent,
  isLoadEvent,
  isLoadEventTimeData,
  isPortCallArrivalEventTimeData,
  isPortCallEvent,
  isSingleEvent,
  isSingleEventTimeData,
  isSpotEvent,
  isTCOrderEvent,
  isTCOrderEventTimeData,
  PortCallEventType,
  SingleEventType,
} from '../core/TimeData';
import type { ScenarioLocation, ScenarioOrder } from '../../lib/planningData';
import type { ScenarioType } from './ScenarioMergeTimeline';
import type { EventTimeline, TimelineFocus } from '../core/Timelines';
import type { TimelineDiffState } from '../ScenarioUtil';
import type { CSSProperties, MutableRefObject } from 'react';
import React, {
  Fragment,
  type JSX,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  type IndicatorData,
  ItemType,
  type ScenarioTimelineGroupFields,
  type ShipmentIntegrationStatus,
  type TimelineCompletedVoyage,
  type ViolationIndicatorData,
  type VoyageAssortment,
  type VoyageLeg,
  type VoyagePortCall,
} from './types';
import type { computeTceForTimeData } from '../shipment/TCE';
import type { VoyagePerformanceMetrics } from '../core/Core.voyage-metrics';
import type { TimelineGroup, TimelineItem } from './react-calendar-timeline';
import { useCurrentUser } from '../../state/zustand';
import {
  isWaitLaycanDefaultAtom,
  useIsMissingPortCostVisibleState,
  useIsViolationVoyagesHighlightedState,
  useIsVoyageAttributeVisibleState,
  useIsVoyageCargoVisibleState,
  useViolationIndicatorBasisState,
  useVoyageBoxProfitState,
  useVoyageBoxTCEState,
  useVoyageBoxVCPState,
  type ViolationIndicatorBasis,
} from '../../state/planning/options';
import CalculationContext from '../contexts/CalculationContext';
import type { UseDropProps } from '../hooks/useDragDrop';
import {
  getDropData,
  peekDragData,
  setDragData,
  setDropEffect,
  useDrop,
} from '../hooks/useDragDrop';
import {
  type Currency,
  EventType,
  type ScenarioTimeline_CoaFragment,
  type ScenarioTimeline_OrganizationFragment,
} from '@seaber/common/graphql/types';
import {
  AssortmentExclusionsIndicator,
  AvailableCapacityIndicator,
  BottomIndicators,
  DifferentFromIntegrationIndicator,
  DraftViolationIndicator,
  indicatorTypeToColor,
  indicatorTypeToLineStyle,
  MaxDwtViolationIndicator,
  OverCapacityIndicator,
  PortCallIndicator,
  PortNameIndicator,
  StaticLineIndicator,
  UnallocatedOrdersIndicator,
  VoyageCharterUpdateIndicator,
  FailedRoutesIndicator,
  type FailedRoutes,
} from './Indicators';
import { hToMs, msToH } from '../../lib/time';
import { EventCategory } from '@seaber/common/core/entries';
import { Box, Tooltip, View } from '../ui';
import { useAtomValue } from 'jotai';
import { groupBySequence, isDefined, useAsRef } from '../../lib/util';
import { shouldWaitForLaycan } from '../PlanningTimelineOptions';
import type { DragDropData, ScenarioTimelineProps } from './ScenarioTimeline';
import { getDroppedOrderIds } from './ScenarioTimeline';
import { UnselectableDiv, VoyageBoxDataContainer, Text } from './Common.styled';
import { COLOR_CONFLICT, contain, fullSize } from './timelineStyles';
import type { ScenarioTimelineItem, VoyageTooltipContent } from './types';
import type { CalculationConfig } from '../core/ScheduleDataContext';
import type { AssortmentExclusionItem } from '../hooks/useAssortmentExclusions';
import type { ClientId } from '@seaber/common/lib/clientId';
import { getUnitSymbol, stringToNumOpt } from '../core/util';
import { compact, startCase, sumBy } from 'lodash';
import uniq from 'lodash/uniq';
import { calculateTimelineFreightCosts } from './utils/TimelineFreightCost';
import { getEventLocation, SwapDirection } from '../core/Core';
import {
  getCargoUnitQuantity,
  systemAmountToCargoUnit,
} from '../../constants/cargoUnits';
import { getOrderContractType } from '../order/OrderTableUtils';
import findLast from 'lodash/findLast';
import { volumeCapacityUnits } from '../../constants/capacityUnits';
import { formatCurrency, formatNumber } from '../../lib/format';
import { dragDropDataSchema } from './utils/utils';
import useEvent from '../hooks/useEvent';
import { timeFieldFormat } from '../ui/TimeField';
import { getDraftWarningAtEvent } from './utils/VesselDraftUtils';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ContextMenuIcon,
  DocumentIcon,
  LockIcon,
  NoteIcon,
  RedCircleIcon,
} from '../Icons';
import { VesselDraftWarning } from './VesselDraftWarning';
import {
  getSpecialRequirementsWarning,
  VesselSpecialRequirementsWarning,
} from './VesselSpecialRequirementsWarning';
import { documentTypeOptions } from '../shipment/PortCallFiles';
import {
  ShipmentAssortmentsField,
  ShipmentCharterersField,
  ShipmentField,
} from './ShipmentField';
import { ProfitTooltip, TceDataTooltip, VoyageTooltip } from './Tooltips';
import { useSingleClickHandlers } from './utils/hooks';
import { useContextProp } from '../contexts/ZustandContext';
import { VoyageMenuContext } from '../contexts/VoyageMenuContext';
import { useSpecialRequirements } from '../hooks/useSpecialRequirements';

/**
 * Wrapper component of the timeline item handles the drop hook so that we can avoid full re-renders when nothing changed.
 */
const TimelineVoyageImpl = <
  T extends {
    pixelsPerMs: number;
    timelineId: EventTimeline['id'];
    locations: ScenarioTimelineProps['scheduleContext']['locations'];
    groupObj?: TimelineGroup<ScenarioTimelineGroupFields> | undefined;
    zIndex?: CSSProperties['zIndex'] | undefined;
    selected?: boolean;
    isShadow?: boolean;
    originalContract?: string;
    getTimelineOffsetTop?: (timeline: EventTimeline) => number;
    timeline?: EventTimeline & {
      isCollapsed?: boolean;
      canCollapse?: boolean;
      coaId?: number;
      coa?: ScenarioTimeline_CoaFragment;
    };
    scrollToTimeline?: (
      timeline: EventTimeline,
      offsetTop?: number | undefined,
    ) => void;
    dragging?: boolean;
    showInquiries?: boolean;
    onViewAssignment?: (timelineFocus?: TimelineFocus) => void;
    onAddOrderToAssignment?: ScenarioTimelineProps['onAddOrderToAssignment'];
    onChangeOrdersVoyage?:
      | ScenarioTimelineProps['onChangeOrdersVoyage']
      | undefined;
    portCallsById?: ScenarioTimelineProps['scheduleContext']['portCalls'];
    shipmentIntegrationStatus?: ShipmentIntegrationStatus;
    item: TimelineItem<{
      events: TimeData[];
      implicitEvents: (Leg | WaitEvent)[];
      isCollapsed: boolean;
      isShared?: boolean;
      onSwapScheduleEntry?: ScenarioTimelineProps['onSwapScheduleEntry'];
      onSwapPortCall?: ScenarioTimelineProps['onSwapPortCall'];
      orders: ScenarioOrder[];
      enablePortCallUpdates?: boolean;
      enableVoyageCharterUpdates?: boolean;
      type?: ItemType.SPOT | ItemType.COA | ItemType.SCHEDULED_ORDER;
      isCompressedView?: ScenarioTimelineProps['isCompressedView'];
      combinedDiff?: TimelineDiffState;
      scenarioType?: ScenarioType;
      tceData?: ReturnType<typeof computeTceForTimeData>;
      metrics?: VoyagePerformanceMetrics;
      organizations: ScenarioTimelineProps['organizations'];
      isFirstShipmentEvent?: boolean;
    }>;
  },
>(
  props: T,
): JSX.Element | null => {
  const {
    onAddOrderToAssignment,
    getTimelineOffsetTop,
    timeline,
    timelineId,
    scrollToTimeline,
    onChangeOrdersVoyage,
    showInquiries,
    item: { orders, events, isCollapsed },
    shipmentIntegrationStatus,
  } = props;
  const onAddOrder:
    | ScenarioTimelineProps['onAddOrderToAssignment']
    | undefined = useMemo(
    () =>
      onAddOrderToAssignment &&
      getTimelineOffsetTop &&
      scrollToTimeline &&
      timeline &&
      (async (...args) => {
        const originalOffset = getTimelineOffsetTop(timeline);
        onAddOrderToAssignment(...args);
        scrollToTimeline(timeline, originalOffset);
      }),
    [onAddOrderToAssignment, getTimelineOffsetTop, timeline, scrollToTimeline],
  );
  const isWaitLaycanDefault = useAtomValue(isWaitLaycanDefaultAtom);
  const onDrop = useAsRef(
    useCallback(
      (item: DragDropData, event: React.DragEvent) => {
        if (item.type === 'orders') {
          const orderIds = getDroppedOrderIds(item);

          const startLoad = new Date(events[0].end);
          if (onAddOrder) {
            onAddOrder(orders[0].id, orderIds, startLoad);
          }
        } else if (item.type === 'shipments') {
          if (orders.some((o) => item.orderIds.includes(o.id))) {
            // shipment/order dropped onto an order in the same shipment (or itself), skipping handling
            return;
          }
          if (onChangeOrdersVoyage) {
            onChangeOrdersVoyage(new Set(item.orderIds), timelineId, {
              startDate: new Date(events[0].end),
              shipWithOrderId: orders[0].id,
              // This can't really be true currently because when ctrl is held we don't activate dropping onto shipments, but maybe it will apply in the future
              voyagePlacement:
                event.ctrlKey || event.metaKey
                  ? 'exact'
                  : shouldWaitForLaycan(event.shiftKey, isWaitLaycanDefault)
                    ? 'laycan'
                    : 'asap',
              isWholeShipment: isCollapsed,
              singleEventIds: events
                .filter(isSingleEventTimeData)
                .map((e) => e.event.entry.clientId),
            });
          }
        }
      },
      [
        isWaitLaycanDefault,
        onAddOrder,
        onChangeOrdersVoyage,
        orders,
        events,
        isCollapsed,
        timelineId,
      ],
    ),
  );

  const {
    dragging,
    locations,
    portCallsById,
    selected,
    zIndex,
    isShadow,
    originalContract,
    pixelsPerMs,
    item: {
      id,
      group,
      start_time,
      end_time,
      enablePortCallUpdates,
      enableVoyageCharterUpdates,
      type,
      implicitEvents,
      isShared,
      onSwapScheduleEntry,
      onSwapPortCall,
      isCompressedView,
      combinedDiff,
      scenarioType,
      tceData,
      metrics,
      organizations,
      isFirstShipmentEvent,
    },
    onViewAssignment,
  } = props;
  return (
    <TimelineVoyageInner
      {...{
        id,
        group,
        dragging,
        end_time,
        events,
        locations,
        portCallsById,
        implicitEvents,
        isCollapsed,
        isShared,
        onSwapScheduleEntry,
        onSwapPortCall,
        organizations,
        selected,
        start_time,
        zIndex,
        timelineId,
        enablePortCallUpdates,
        enableVoyageCharterUpdates,
        type,
        isShadow,
        originalContract,
        isCompressedView,
        combinedDiff,
        scenarioType,
        orders,
        onDrop,
        showInquiries,
        tceData,
        metrics,
        onViewAssignment,
        pixelsPerMs,
        shipmentIntegrationStatus,
        isFirstShipmentEvent,
      }}
    />
  );
};
const TimelineVoyageInnerImpl = <
  T extends {
    id: ScenarioTimelineItem['id'];
    events: TimeData[];
    orders: ScenarioOrder[];
    originalContract?: string;
    scenarioType?: ScenarioType;
    timelineId: EventTimeline['id'];
    combinedDiff?: TimelineDiffState;
    implicitEvents: (Leg | WaitEvent)[];
    zIndex?: CSSProperties['zIndex'] | undefined;
    onDrop: MutableRefObject<
      (item: DragDropData, event: React.DragEvent) => void
    >;
    type?: ItemType.SPOT | ItemType.COA | ItemType.SCHEDULED_ORDER;
    enableVoyageCharterUpdates?: boolean;
    enablePortCallUpdates?: boolean;
    showInquiries?: boolean;
    selected?: boolean;
    dragging?: boolean;
    isShared?: boolean;
    isShadow?: boolean;
    isCollapsed: boolean;
    pixelsPerMs: number;
    tceData?: ReturnType<typeof computeTceForTimeData>;
    metrics?: VoyagePerformanceMetrics;
    onViewAssignment?: (timelineFocus?: TimelineFocus) => void;
    organizations?: ScenarioTimelineProps['organizations'];
    isCompressedView?: ScenarioTimelineProps['isCompressedView'];
    onSwapScheduleEntry?: ScenarioTimelineProps['onSwapScheduleEntry'];
    onSwapPortCall?: ScenarioTimelineProps['onSwapPortCall'];
    portCallsById?: ScenarioTimelineProps['scheduleContext']['portCalls'];
    locations: ScenarioTimelineProps['scheduleContext']['locations'];
    shipmentIntegrationStatus?: ShipmentIntegrationStatus;
    isFirstShipmentEvent?: boolean;
  },
>(
  props: TimelineItem<T>,
): JSX.Element | null => {
  const {
    id,
    combinedDiff,
    dragging,
    enablePortCallUpdates = false,
    enableVoyageCharterUpdates = false,
    events,
    implicitEvents,
    isCompressedView,
    isShadow,
    isShared,
    locations,
    portCallsById,
    metrics,
    onDrop,
    onSwapPortCall,
    onSwapScheduleEntry,
    orders,
    organizations,
    originalContract,
    scenarioType,
    selected,
    showInquiries,
    start_time,
    tceData,
    timelineId,
    type,
    zIndex = 0,
    onViewAssignment,
    pixelsPerMs,
    shipmentIntegrationStatus,
    isFirstShipmentEvent,
    isCollapsed,
  } = props;
  const currentUser = useCurrentUser();
  const isShipowner = currentUser?.organization?.shipOwner;
  const baseCurrency =
    currentUser?.organization?.defaultCurrency ||
    currentUser?.rootOrganization?.defaultCurrency;
  const [violationIndicatorBasis] = useViolationIndicatorBasisState();
  const calculationConfig = useContext(CalculationContext)?.calculationConfig;
  const {
    assortments,
    charterers,
    dragData,
    failedRouteLegs,
    firstLoad,
    firstLoadIndex,
    indicators,
    isCreatedInVMS,
    lastDischarge,
    lastDischargeIndex,
    legsByOriginPortCall,
    orderRemarks,
    portCalls,
    shipmentId,
    shipmentRemarks,
    showDischargeToLeftSwap,
    showDischargeToRightSwap,
    showLoadToLeftSwap,
    showLoadToRightSwap,
    violationIndicators,
    assortmentExclusionViolations: assortmentExclusions,
    vesselWeightCapacity,
    voyageCharterId,
    voyageTooltip,
    colorCode,
  } = useMemo(
    () =>
      deriveVoyageData(
        calculationConfig,
        orders,
        events,
        locations,
        implicitEvents,
        combinedDiff,
        scenarioType,
        violationIndicatorBasis,
        isShadow,
        type,
        originalContract,
        props.isCollapsed,
        organizations,
        isShipowner,
        baseCurrency,
        metrics,
        portCallsById,
      ),
    [
      baseCurrency,
      calculationConfig,
      combinedDiff,
      events,
      implicitEvents,
      isShadow,
      isShipowner,
      locations,
      metrics,
      orders,
      organizations,
      originalContract,
      portCallsById,
      props.isCollapsed,
      scenarioType,
      type,
      violationIndicatorBasis,
    ],
  );

  const elRef = useRef<HTMLElement | null>(null);

  const [hoveredShipmentItem, setHoveredShipmentItem] = useState<number | null>(
    null,
  );

  const dropSpec: UseDropProps = useMemo(
    () =>
      ({
        onDrop: (e) => {
          const item = getDropData(e, dragDropDataSchema.parse);
          if (item) {
            onDrop.current(item, e);
          }
        },
        onDragOver: () => {
          setDropEffect('copy', 'Add to shipment');
        },
        accept: (e) => {
          if (e.ctrlKey || e.metaKey) {
            setDropEffect(
              'none',
              'Cannot use modifiers when adding to shipment',
            );
            return false;
          }
          const data = peekDragData(e, dragDropDataSchema.parse);
          if (data?.type === 'shipments') {
            // Don't accept drop on self
            const draggedOrders = data.orderIds;
            if (
              draggedOrders.some((droppedOrderId) =>
                props.orders.find((o) => o.id === droppedOrderId),
              )
            ) {
              return false;
            }
          }
          return true;
        },
      }) as UseDropProps,
    [onDrop, props.orders],
  );

  const { isDragOver, props: dropProps } = useDrop(dropSpec);

  /** We need to intercept the normal mouse events, but also need to be able to send mousedown & mouseup when a click happens.
   * When this is set, the mouse event handlers will let the event flow to parent (allowing timeline component to perform item de-/selection). */
  const clickPassThrough = useRef<boolean>(false);

  const staticIndicators = useMemo(
    () =>
      indicators.map((indicator, index) => (
        <StaticLineIndicator
          key={index}
          color={
            indicator.type === 'single' &&
            indicator.subtype === EventType.WAITING
              ? 'orange.orange_600'
              : indicatorTypeToColor[indicator.type]
          }
          offset={(indicator.startTime - start_time) * pixelsPerMs}
          width={(indicator.totalDuration || indicator.duration) * pixelsPerMs}
          lineStyle={indicatorTypeToLineStyle[indicator.type]}
          tooltipDisabled={isDragOver}
          indicatorLineWidth={
            (indicator.type === 'load' ||
              indicator.type === 'discharge' ||
              indicator.type === 'single') &&
            hoveredShipmentItem === indicator.startTime
              ? '8px'
              : '4px'
          }
          tooltip={{
            title:
              startCase(indicator.subtype?.toLowerCase() ?? indicator.type) +
              (indicator.portName ? ` at ${indicator.portName}` : ''),
            startTime: indicator.startTime,
            duration: msToH(indicator.duration),
            portName: indicator.portName,
            timezone: indicator.timezone,
            assortmentName: indicator.assortmentName,
            from: indicator.from,
            to: indicator.to,
            amount: indicator.amount ?? 0,
            amountUnit: indicator.amountUnit ?? undefined,
            totalDuration: msToH(indicator.totalDuration ?? 0),
            assortments: indicator.assortments,
          }}
          onMouseEnter={() => setHoveredShipmentItem(indicator.startTime)}
          onMouseLeave={() => setHoveredShipmentItem(null)}
        />
      )),
    [indicators, start_time, pixelsPerMs, isDragOver, hoveredShipmentItem],
  );

  const firstEventLocationId = useMemo(() => {
    const firstEvent = events[0]?.event;

    return firstEvent &&
      firstEvent.category !== EventCategory.OffHire &&
      firstEvent.category !== EventCategory.Static
      ? firstEvent.locationId
      : undefined;
  }, [events]);

  const staticViolationIndicators = useMemo<
    JSX.Element | JSX.Element[] | null
  >(() => {
    if (isCollapsed) {
      const uniqueViolationIndicators = new Map<
        string,
        ViolationIndicatorData
      >();
      const existingKeys = new Set<string>();
      for (const indicator of violationIndicators) {
        const voyageCharterId = indicator.voyageCharterId;
        const locationId = indicator.locationId;
        const key = `${indicator.type}-${voyageCharterId ?? locationId}`;
        if (existingKeys.has(key)) {
          continue;
        }
        existingKeys.add(key);
        uniqueViolationIndicators.set(key, indicator);
      }

      return [...uniqueViolationIndicators.values()].map((indicator, index) => {
        const key = `${indicator.type}-${indicator.locationId}-${index}`;
        return (
          <StaticLineIndicator
            key={key}
            color="voyageEvents.violation"
            opacity={0.7}
            width={Math.abs(indicator.duration * pixelsPerMs)}
            offset={
              (indicator.startTime -
                start_time -
                (indicator.duration > 0 ? indicator.duration : 0)) *
              pixelsPerMs
            }
            lineStyle="solid"
            tooltip={{
              title:
                indicator.type.split('_').map(startCase).join(' ') +
                (indicator.portName ? ` in ${indicator.portName}` : ''),
              duration: msToH(indicator.duration),
              type: indicator.type,
            }}
          />
        );
      });
    } else {
      const indicator = violationIndicators.find(
        (indicator) => indicator.locationId === firstEventLocationId,
      );

      // Prevent showing violation indicator on any other shipment than the first one
      if (!indicator || !isFirstShipmentEvent) {
        return null;
      }

      return (
        <StaticLineIndicator
          key={indicator.locationId}
          color="voyageEvents.violation"
          opacity={0.7}
          width={Math.abs(indicator.duration * pixelsPerMs)}
          offset={
            (indicator.startTime -
              start_time -
              (indicator.duration > 0 ? indicator.duration : 0)) *
            pixelsPerMs
          }
          lineStyle="solid"
          tooltip={{
            title:
              indicator.type.split('_').map(startCase).join(' ') +
              (indicator.portName ? ` in ${indicator.portName}` : ''),
            duration: msToH(indicator.duration),
            type: indicator.type,
          }}
        />
      );
    }
  }, [
    isCollapsed,
    violationIndicators,
    isFirstShipmentEvent,
    pixelsPerMs,
    start_time,
    firstEventLocationId,
  ]);

  const [isViolationVoyagesHighlighted] =
    useIsViolationVoyagesHighlightedState();

  if (events.every(isSingleEventTimeData)) {
    // Currently not rendering orphan single events at all
    return null;
  }

  if (events.length < 2) {
    throw new Error(
      `Rendering timeline shipment group with too few (${events.length}) events`,
    );
  }

  const hasAnyViolation = violationIndicators.length > 0;

  return (
    <UnselectableDiv
      style={{
        height: '100%',
        width: '100%',
        opacity:
          isViolationVoyagesHighlighted && !hasAnyViolation ? '40%' : undefined,
        outline:
          isViolationVoyagesHighlighted && hasAnyViolation
            ? '1px solid red'
            : undefined,
      }}
    >
      <View width={1} height="100%" bg="neutral.white_50">
        {/* The actual box */}
        <ShipmentBox
          {...{
            id,
            assortments,
            failedRouteLegs,
            baseCurrency,
            borderColor: colorCode,
            charterers,
            clickPassThrough,
            combinedDiff,
            dragData,
            dropProps,
            elRef,
            enablePortCallUpdates,
            enableVoyageCharterUpdates,
            isCompressedView,
            isDragOver,
            isSelected: selected ?? false,
            isShared,
            isCreatedInVMS,
            legsByOriginPortCall,
            locations,
            metrics,
            onSwapPortCall,
            orderRemarks,
            portCalls,
            scenarioType,
            shipmentId,
            shipmentRemarks,
            showInquiries,
            tceData,
            timelineId,
            type,
            assortmentExclusions,
            voyageCharterId,
            voyageTooltip,
            onViewAssignment,
            shipmentIntegrationStatus,
            setHoveredShipmentItem,
            hoveredShipmentItem,
            vesselWeightCapacity,
          }}
        />
        {/* Bottom indicators */}
        <BottomIndicators
          {...{
            dragging,
            firstLoad,
            firstLoadIndex,
            isShared,
            lastDischarge,
            lastDischargeIndex,
            onSwapScheduleEntry,
            selected,
            showDischargeToLeftSwap,
            showDischargeToRightSwap,
            showLoadToLeftSwap,
            showLoadToRightSwap,
            staticIndicators,
            staticViolationIndicators,
            timelineId,
            zIndex,
          }}
        />
      </View>
    </UnselectableDiv>
  );
};
export const TimelineVoyagePlaceholder = React.memo(
  ({
    combinedDiff,
    scenarioType,
    isCompressedView,
    isShared,
  }: {
    combinedDiff?: TimelineDiffState;
    scenarioType?: ScenarioType;
    isCompressedView?: boolean;
    isShared?: boolean;
  }) => {
    return (
      <UnselectableDiv style={fullSize}>
        <View width={1} height="100%" bg="neutral.white_50">
          <Box display="flex" flexDirection="column" height="100%">
            <View
              flexDirection="row"
              bg={
                (combinedDiff?.hasOverlap || combinedDiff?.hasConflict) &&
                scenarioType !== 'master'
                  ? COLOR_CONFLICT
                  : combinedDiff?.masterUpdated ||
                      combinedDiff?.masterDeleted ||
                      combinedDiff?.scenarioDeleted
                    ? 'orderStatus.otherUpdated'
                    : scenarioType !== 'master' &&
                        combinedDiff?.hasChange &&
                        combinedDiff.scenarioControlled
                      ? 'orderStatus.assigned'
                      : isShared
                        ? 'orderStatus.shared'
                        : 'orderStatus.committed'
              }
              overflow="hidden"
              height="20px"
              alignItems="center"
              justifyContent="space-between"
            />
            {!isCompressedView && (
              <Box
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                px={2}
                height="18px"
                bg="#ced5e0"
              />
            )}
            <View height="4px" width={1} bg="neutral.white_50" />
          </Box>
        </View>
      </UnselectableDiv>
    );
  },
);
export const TimelineVoyageRenderer = React.memo(TimelineVoyageImpl);
const TimelineVoyageInner = React.memo(TimelineVoyageInnerImpl);
const getOrganizationName = (
  organizations: ScenarioTimelineProps['organizations'] | undefined,
  id: number | null | undefined,
) => {
  return organizations && id ? (organizations.get(id)?.name ?? '') : '';
};

function deriveVoyageData(
  calculationConfig: CalculationConfig | null,
  orders: ScenarioOrder[],
  events: TimeData[],
  locations: Map<number, ScenarioLocation>,
  implicitEvents: (Leg | WaitEvent)[],
  combinedDiff: TimelineDiffState | undefined,
  scenarioType: 'master' | 'scenario' | 'commitresult' | undefined,
  violationIndicatorBasis: ViolationIndicatorBasis,
  isShadow: undefined | boolean,
  type: ItemType.SPOT | ItemType.COA | ItemType.SCHEDULED_ORDER | undefined,
  originalContract: string | undefined,
  isCollapsed: boolean,
  organizations: Map<number, ScenarioTimeline_OrganizationFragment> | undefined,
  isShipowner?: boolean,
  baseCurrency?: Currency | null,
  metrics?: VoyagePerformanceMetrics,
  portCallsById?: ScenarioTimelineProps['scheduleContext']['portCalls'],
): {
  showDischargeToRightSwap: boolean;
  voyageCharterId: number | null;
  showDischargeToLeftSwap: undefined | boolean;
  lastDischarge: TimeData | undefined;
  assortments: Array<VoyageAssortment>;
  assortmentExclusionViolations: AssortmentExclusionItem[];
  indicators: Array<IndicatorData>;
  dragData: DragDropData;
  lastDischargeIndex: number | undefined;
  showLoadToLeftSwap: boolean;
  showLoadToRightSwap: undefined | boolean;
  firstLoadIndex: number | undefined;
  violationIndicators: Array<ViolationIndicatorData>;
  firstLoad: TimeData | undefined;
  shipmentId: ScenarioOrder['shipmentId'];
  portCalls: Array<VoyagePortCall>;
  voyageTooltip: VoyageTooltipContent;
  orderRemarks: string[];
  shipmentRemarks: string;
  isCreatedInVMS: boolean;
  charterers: ScenarioTimeline_OrganizationFragment[];
  legsByOriginPortCall: Map<ClientId, VoyageLeg>;
  colorCode: string | null;
  vesselWeightCapacity: number | null;
  failedRouteLegs: FailedRoutes;
} {
  const indicators: Array<IndicatorData> = [];
  const violationIndicators: Array<ViolationIndicatorData> = [];
  const failedRouteLegs: FailedRoutes = [];

  const portCalls: Array<VoyagePortCall> = [];
  const legsByOriginPortCall: Map<ClientId, VoyageLeg> = new Map();

  const assortments: Array<VoyageAssortment> = [];
  const voyageAssortmentExclusions = new Set<AssortmentExclusionItem>();

  const shipment = orders.at(0)?.shipment;
  const shipmentId = shipment?.clientId ?? null;
  const voyageName = shipment?.name ?? undefined;
  const voyageAttributeId = shipment?.voyageAttributeId ?? null;
  const externalShipmentId = shipment?.externalShipmentId ?? undefined;
  const voyageAttribute = calculationConfig?.voyageAttributeById.get(
    voyageAttributeId ?? NaN,
  );
  const colorCode = voyageAttribute?.isActive ? voyageAttribute.color : null;

  const vesselEvent = events.find((x) => !!x.event.vessel);
  const vesselName = vesselEvent?.event.vessel?.name ?? 'Vessel TBD';
  const vesselCapacity = stringToNumOpt(
    vesselEvent?.event.vessel?.massCapacity,
  );
  const vesselCapacityUnit = vesselEvent?.event.vessel?.massUnit;
  const vesselVolume = stringToNumOpt(
    vesselEvent?.event.vessel?.volumeCapacity,
  );

  const vesselWeightCapacity = stringToNumOpt(
    vesselEvent?.event.vessel?.weightCapacity,
  );

  const vesselVolumeUnit = vesselEvent?.event.vessel?.volumeUnit;

  const voyageHasAnyTankAllocations = events.some(
    (td) =>
      isTCOrderEvent(td.event) &&
      td.event.cargoAllocations.some((a) => +a.amount > 0),
  );
  const currentlyLoadedOrders = new Set<ScenarioOrder['id']>();
  const currentlyLoadedTdsByOrderId = new Map<number, TimeData<CargoEvent>>();
  let currentlyLoadedMass = 0;
  let currentlyLoadedVolume = 0;
  let totalPlannedVolume = 0;
  let totalPlannedMass = 0;

  groupBySequence(events, (td) => getEventLocationId(td.event)).forEach(
    (locationTds) => {
      const canSwapLeftShipments = compact(
        uniq(
          locationTds
            .filter(isCargoEventTimeData)
            .filter((td) => td.canPortCallSwapLeft)
            .map((td) => td.event.order.shipmentId),
        ),
      );
      const canSwapRightShipments = compact(
        uniq(
          locationTds
            .filter(isCargoEventTimeData)
            .filter((td) => td.canPortCallSwapRight)
            .map((td) => td.event.order.shipmentId),
        ),
      );

      const etaTD = locationTds.find(isPortCallArrivalEventTimeData);

      locationTds.forEach((td) => {
        const { event } = td;

        if (
          !isTCOrderEvent(event) &&
          !isSingleEvent(event) &&
          !isSpotEvent(event) &&
          !isCoaEvent(event) &&
          !isPortCallEvent(event)
        ) {
          throw new Error(
            `Bad event type for TimelineVoyage component: ${event.category}`,
          );
        }

        const { arriveInPort, start, end, assortmentExclusions } = td;

        for (const assortmentExclusion of assortmentExclusions) {
          voyageAssortmentExclusions.add(assortmentExclusion);
        }

        const relatedOrder = 'order' in td.event ? td.event.order : null;

        const portCallId = event.portCallId;
        const portCall = portCallId ? portCallsById?.get(portCallId) : null;
        const portCallFiles =
          portCall?.portCallFilesByPortCallId.nodes
            .flatMap((i) => i?.fileByFileId)
            .filter(isDefined)
            .map((i) => ({ id: i.id, type: i.type, note: i?.note })) ?? [];
        const port = getEventLocation(event, locations);
        const prevPortCall = portCalls[portCalls.length - 1] ?? null;
        const prevPortCallId = prevPortCall?.clientId ?? null;
        const isNewPort = prevPortCall?.portId !== port?.id;

        // If arrived in new port, assume a new port call

        if (
          isNewPort &&
          (shipmentId === relatedOrder?.shipmentId || isSingleEventTimeData(td))
        ) {
          portCalls.push({
            portId: port?.id ?? NaN,
            portName: port?.name ?? 'Port name N/A',
            portCallFiles,
            startTime: arriveInPort,
            clientId: portCallId,
            canSwapLeftShipments,
            canSwapRightShipments,
            events: locationTds,
            hasCargoEvents: locationTds.some(isCargoEventTimeData),
            orderId: relatedOrder?.id,
            start: start,
            maxDwt: stringToNumOpt(port?.maxDwt) ?? null,
          });
        }

        if (
          isNewPort &&
          portCallId &&
          prevPortCallId &&
          !legsByOriginPortCall.has(portCallId)
        ) {
          const amountNotLoadedPerOrder = [
            ...currentlyLoadedTdsByOrderId.values(),
          ]
            .filter((td) => td.amount < 1)
            .map(
              (td) =>
                [
                  td.event.order.id,
                  {
                    mass: Math.max(
                      0,
                      td.event.order.cargoRanges.mass.min - td.mass,
                    ),
                    volume: Math.max(
                      0,
                      td.event.order.cargoRanges.volume.min - td.holdVolume,
                    ),
                  },
                ] as const,
            );

          const totalMassNotLoaded = sumBy(
            amountNotLoadedPerOrder,
            ([, data]) => data.mass,
          );
          const totalVolumeNotLoaded = sumBy(
            amountNotLoadedPerOrder,
            ([, data]) => data.volume,
          );

          /** Any orders having 0 allocations, but only relevant if allocations are in use at all in this voyage. */
          const isMissingCargoAllocations =
            voyageHasAnyTankAllocations &&
            [...currentlyLoadedTdsByOrderId.values()].some(
              (td) =>
                isTCOrderEvent(td.event) &&
                td.event.cargoAllocations.every((a) => +a.amount === 0),
            );

          legsByOriginPortCall.set(prevPortCallId, {
            originPortCallId: prevPortCallId,
            originPortName: prevPortCall.portName,
            destinationPortCallId: portCallId,
            destinationPortName: port?.name ?? 'Port name N/A',
            availableMassCapacity: vesselCapacity
              ? vesselCapacity - currentlyLoadedMass
              : vesselWeightCapacity
                ? vesselWeightCapacity - currentlyLoadedMass
                : null,
            availableMassCapacityPercentage: vesselCapacity
              ? ((vesselCapacity - currentlyLoadedMass) / vesselCapacity) * 100
              : vesselWeightCapacity
                ? ((vesselWeightCapacity - currentlyLoadedMass) /
                    vesselWeightCapacity) *
                  100
                : null,
            availableVolumeCapacity: vesselVolume
              ? vesselVolume - currentlyLoadedVolume
              : null,
            availableVolumeCapacityPercentage: vesselVolume
              ? ((vesselVolume - currentlyLoadedVolume) / vesselVolume) * 100
              : null,
            overCapacityMass: totalMassNotLoaded,
            overCapacityVolume: totalVolumeNotLoaded,
            hasUnallocatedOrders: isMissingCargoAllocations,
          });
        }

        if (isCargoEventTimeData(td)) {
          if (isLoadEventTimeData(td)) {
            currentlyLoadedOrders.add(td.event.order.id);
            currentlyLoadedTdsByOrderId.set(td.event.order.id, td);
          } else {
            currentlyLoadedOrders.delete(td.event.order.id);
            currentlyLoadedTdsByOrderId.delete(td.event.order.id);
          }

          currentlyLoadedMass += td.mass;
          currentlyLoadedVolume += td.holdVolume;
        }

        const cargoUnit = relatedOrder?.cargoUnitByCargoUnitId?.name || 'mts';
        const cargoAmount = cargoUnit
          ? getCargoUnitQuantity(cargoUnit ?? '') === 'volume'
            ? systemAmountToCargoUnit(Math.abs(td.holdVolume), cargoUnit ?? '')
            : systemAmountToCargoUnit(Math.abs(td.mass), cargoUnit ?? '')
          : +(relatedOrder?.cargoAmount ?? 0);

        const assortment = relatedOrder?.assortmentByAssortmentId;

        if (
          event.type === CoreEventType.Load &&
          relatedOrder &&
          isDefined(cargoAmount)
        ) {
          assortments.push({
            assortmentName: assortment?.name ?? 'Commodity name N/A',
            amount: cargoAmount,
            amountUnit: cargoUnit,
            incomingContractType: getOrderContractType(relatedOrder),
            cargoDeckOption:
              relatedOrder.orderDetailsByOrderId.nodes[0]?.cargoDeckOption ??
              null,
          });

          totalPlannedVolume += td.holdVolume;
          totalPlannedMass += td.mass;
        }

        // Actual event
        const eventType =
          event.category === EventCategory.PortCall
            ? 'portCall'
            : event.type === CoreEventType.Single
              ? 'single'
              : event.type === CoreEventType.Load
                ? 'load'
                : 'discharge';

        const prevIndicator =
          indicators.length > 0 ? indicators[indicators.length - 1] : null;

        const orderId = relatedOrder?.id;
        const isSameIndicatorType =
          prevIndicator &&
          eventType === prevIndicator.type &&
          port.name === prevIndicator.portName;

        if (
          (isSameIndicatorType && !('event' in event)) ||
          (isSameIndicatorType &&
            'event' in event &&
            event.event === prevIndicator.rowType)
        ) {
          const currentAssortment = {
            assortmentName:
              relatedOrder?.assortmentByAssortmentId?.name ?? undefined,
            duration: end - start > 60 ? msToH(end - start) : 0,
            amount: cargoAmount,
            amountUnit: cargoUnit,
            orderId,
          };

          if (
            prevIndicator.orderId !== orderId &&
            !prevIndicator.assortments?.some((a) => a.orderId === orderId)
          ) {
            (prevIndicator.assortments ??= []).push(currentAssortment);
          }

          const totalDuration = end - prevIndicator.startTime;
          prevIndicator.totalDuration = Math.max(
            totalDuration,
            prevIndicator.duration,
          );
        } else {
          const rowType =
            isPortCallEvent(event) && end - start > 0
              ? event.event === PortCallEventType.Arrival
                ? 'pilotage/anchorage'
                : event.event === PortCallEventType.Berthing
                  ? 'berthed/preparations'
                  : undefined
              : undefined;
          indicators.push({
            type: eventType,
            rowType,
            subtype:
              event.category === EventCategory.Single
                ? SingleEventType[event.event].toString()
                : undefined,
            duration: end - start,
            startTime: start,
            portName: port?.name ?? undefined,
            timezone: port?.timezone ?? undefined,
            assortmentName:
              relatedOrder?.assortmentByAssortmentId?.name ?? undefined,
            amount: relatedOrder?.cargoAmount
              ? +relatedOrder.cargoAmount * td.amount
              : 0,
            amountUnit: relatedOrder?.cargoUnitByCargoUnitId?.name || 'mts',
            totalDuration: end - start,
            orderId,
          });
        }

        const cancellingOrDeliveryViolation =
          violationIndicatorBasis === 'none'
            ? 0
            : violationIndicatorBasis === 'eta'
              ? td.cancellingOrDeliveryViolationVsEta
              : td.cancellingOrDeliveryViolationVsOperations;

        if (
          (isLoadEvent(event) || isDischargeEvent(event)) &&
          cancellingOrDeliveryViolation !== 0
        ) {
          const isLoadType = event.type === CoreEventType.Load;
          const operationsStart = td.operationsStart ?? start;
          const startTime =
            violationIndicatorBasis === 'eta'
              ? (etaTD?.start ?? operationsStart)
              : operationsStart;
          violationIndicators.push({
            type: isLoadType ? 'load_violation' : 'discharge_violation',
            startTime,
            duration: hToMs(cancellingOrDeliveryViolation),
            portName: port?.name ?? '',
            locationId: getEventLocationId(event),
            voyageCharterId:
              relatedOrder?.incomingVoyageCharterRelation?.voyageCharterId,
          });
        }
      });
    },
  );

  implicitEvents.forEach((ev) => {
    if (ev.category === EventCategory.Wait) {
      indicators.push({
        type: 'idle',
        duration: ev.end - ev.start,
        startTime: ev.start,
        portName: ev.locationName,
      });
    } else if (ev.category === EventCategory.Leg) {
      // Collect legs with missing distance for warning display
      if (ev.distance === 0 && ev.from.id !== ev.to.id) {
        failedRouteLegs.push({
          from: ev.from.name || 'N/A',
          to: ev.to.name || 'N/A',
        });
      }
      indicators.push({
        type: ev.laden ? 'laden' : 'ballast',
        from: ev.from.name || '',
        to: ev.to.name || '',
        duration: ev.end - ev.start,
        startTime: ev.start,
        timezone: ev.from.timezone || undefined,
      });
    }
  });

  const firstLoad = events.find((td) => !td.event.skipOnSwap);
  const lastDischarge = findLast(events, (td) => !td.event.skipOnSwap);
  const firstLoadIndex = firstLoad?.index;
  const lastDischargeIndex = lastDischarge?.index;

  const hasMiddle = firstLoad?.hasMiddle;
  const showLoadToLeftSwap = !firstLoad?.isFirstEvent;
  const showLoadToRightSwap = hasMiddle;
  const showDischargeToLeftSwap = hasMiddle;
  const showDischargeToRightSwap = !lastDischarge?.isLastEvent;

  const baseTotalCost = sumBy(events, (event) => event.costs.totalCost ?? 0);

  const isReletShipment =
    type === ItemType.SPOT &&
    isShipowner &&
    orders.some((order) => order.outgoingVoyageCharterRelation);

  const freightCostTotal = isReletShipment
    ? calculateTimelineFreightCosts(events, orders)
    : 0;

  const totalCost = baseTotalCost + freightCostTotal;

  const isCreatedInVMS = orders.some((o) => o.shipment?.externalShipmentStatus);
  const voyageTooltip: VoyageTooltipContent = {
    title: combinedDiff?.hasConflict
      ? 'The assignment has been updated both in committed data and your scenario in a conflicting way. Reset the assignment in your scenario to use the current version or confirm your scenario version before commit.'
      : combinedDiff?.hasOverlap && scenarioType === 'scenario'
        ? 'The assignment overlaps with another assignment.'
        : isShadow && type === ItemType.COA
          ? `This voyage is planned on COA ${originalContract}`
          : isShadow && type === ItemType.SPOT
            ? `This voyage is planned on SPOT`
            : isCreatedInVMS
              ? 'Voyage Details (Created in VMS)'
              : 'Voyage Details',
    voyageName,
    vesselName,
    assigner:
      orders[0]?.scheduledOrdersByOrderId?.nodes[0]?.userByCreatedByUserId
        ?.name ?? '',
    ...{
      vesselOrders: !isShipowner
        ? orders.map((x) =>
            [
              `#${x.orderDetailsByOrderId.nodes[0]?.orderName || x.id} ` +
                `${x.assortmentByAssortmentId?.name}`,
              `${getOrganizationName(
                organizations,
                x.chartererOrganizationId,
              )}`,
              `${x.cargoAmount} ${x.cargoUnitByCargoUnitId?.name}`,
              `${x.locationByLoadPortId?.name} - ${x.locationByDischargePortId?.name}`,
            ]
              .filter((value) => value)
              .join(', '),
          )
        : [],
      vesselCapacity:
        !isShipowner && vesselCapacity && vesselCapacityUnit
          ? `${vesselCapacity} ${getUnitSymbol(vesselCapacityUnit)}`
          : '',
      vesselVolume:
        !isShipowner &&
        vesselVolume &&
        vesselVolumeUnit &&
        volumeCapacityUnits[vesselVolumeUnit]
          ? `${vesselVolume} ${getUnitSymbol(vesselVolumeUnit)}`
          : '',
      totalPlannedVolume:
        !isShipowner && totalPlannedVolume
          ? totalPlannedVolume.toFixed(2) + ' cbm'
          : '',
      totalPlannedMass:
        !isShipowner && totalPlannedMass
          ? totalPlannedMass.toFixed(2) + ' mts'
          : '',
      massCapacityUsed:
        !isShipowner && vesselCapacity
          ? ((totalPlannedMass / vesselCapacity) * 100).toFixed(2) + '%'
          : '',
      totalCost: isShipowner
        ? (formatCurrency(totalCost, 0, baseCurrency) ?? '')
        : '',
      vcp: metrics?.vcp && +metrics?.vcp,
      externalShipmentId,
    },
  };

  const voyageCharterId =
    orders[0]?.outgoingVoyageCharterRelation?.voyageCharterId ?? null;

  const shipmentRemarks = [
    ...new Set(orders.map((x) => x.shipment?.remarks ?? '')),
  ].join();

  const orderRemarks = orders.flatMap((x) =>
    x.orderDetailsByOrderId.nodes
      .flatMap((n) => n?.extraInfo || null)
      .filter(isDefined),
  );

  const dragData: DragDropData = {
    type: 'shipments',
    orderIds: events
      .filter(isCargoEventTimeData)
      .map((td) => td.event.order.id),
    singleEventIds: events
      .filter(isSingleEventTimeData)
      .map((td) => td.event.entry.clientId),
    isWholeShipment: isCollapsed,
  };

  const chartererIds = orders.map((o) => o.chartererOrganizationId);
  const charterers = compact(
    chartererIds.map((orgId) =>
      organizations && orgId != null ? organizations.get(orgId) : null,
    ),
  );

  return {
    assortmentExclusionViolations: [...voyageAssortmentExclusions],
    colorCode,
    indicators,
    violationIndicators,
    shipmentId,
    portCalls,
    assortments,
    charterers,
    firstLoad,
    lastDischarge,
    firstLoadIndex,
    lastDischargeIndex,
    showLoadToLeftSwap,
    showLoadToRightSwap,
    showDischargeToLeftSwap,
    showDischargeToRightSwap,
    voyageTooltip,
    voyageCharterId,
    orderRemarks,
    shipmentRemarks,
    isCreatedInVMS,
    dragData,
    legsByOriginPortCall,
    vesselWeightCapacity: vesselWeightCapacity ?? null,
    failedRouteLegs,
  };
}

const ShipmentBox = React.memo(function ShipmentBox(props: {
  id: ScenarioTimelineItem['id'];
  assortments: Array<VoyageAssortment>;
  baseCurrency: Currency | null | undefined;
  borderColor: string | null;
  charterers: ScenarioTimeline_OrganizationFragment[];
  clickPassThrough: React.MutableRefObject<boolean>;
  combinedDiff: TimelineDiffState | undefined;
  dragData: DragDropData;
  dropProps: Pick<
    React.DOMAttributes<unknown>,
    'onDragEnter' | 'onDrop' | 'onDragLeave' | 'onDragOver'
  >;
  elRef: React.MutableRefObject<HTMLElement | null>;
  enablePortCallUpdates: boolean;
  enableVoyageCharterUpdates: boolean;
  isCompressedView: boolean | undefined;
  isDragOver: boolean;
  isSelected: boolean;
  isShared: boolean | undefined;
  legsByOriginPortCall: Map<ClientId, VoyageLeg>;
  locations: Map<number, ScenarioLocation>;
  metrics?: VoyagePerformanceMetrics;
  onSwapPortCall?:
    | ((
        timelineId: EventTimeline['id'],
        portCallId: ClientId,
        shipmentIds: Array<ScenarioOrder['shipmentId']>,
        direction: SwapDirection,
      ) => void)
    | undefined;
  orderRemarks: string[];
  portCalls: Array<VoyagePortCall>;
  scenarioType: 'master' | 'scenario' | 'commitresult' | undefined;
  shipmentId: ScenarioOrder['shipmentId'];
  shipmentRemarks: string;
  showInquiries?: boolean;
  tceData?: ReturnType<typeof computeTceForTimeData>;
  timelineId: string;
  type?: ItemType.SPOT | ItemType.COA | ItemType.SCHEDULED_ORDER;
  assortmentExclusions: AssortmentExclusionItem[];
  voyageCharterId: number | null;
  voyageTooltip: VoyageTooltipContent;
  isCreatedInVMS: boolean;
  onViewAssignment?: (timelineFocus?: TimelineFocus) => void;
  shipmentIntegrationStatus?: ShipmentIntegrationStatus;
  hoveredShipmentItem: number | null;
  setHoveredShipmentItem: React.Dispatch<React.SetStateAction<number | null>>;
  vesselWeightCapacity: number | null;
  failedRouteLegs: FailedRoutes;
}) {
  const {
    id,
    assortments,
    baseCurrency,
    borderColor,
    charterers,
    clickPassThrough,
    combinedDiff,
    failedRouteLegs,
    dragData,
    dropProps,
    elRef,
    enablePortCallUpdates,
    enableVoyageCharterUpdates,
    isCompressedView,
    isDragOver,
    isShared,
    legsByOriginPortCall,
    locations,
    metrics,
    onSwapPortCall,
    orderRemarks,
    portCalls,
    scenarioType,
    shipmentId,
    shipmentRemarks,
    showInquiries,
    tceData,
    timelineId,
    type,
    assortmentExclusions,
    voyageCharterId,
    voyageTooltip,
    isCreatedInVMS,
    onViewAssignment,
    shipmentIntegrationStatus,
    isSelected,
    setHoveredShipmentItem,
    hoveredShipmentItem,
    vesselWeightCapacity,
  } = props;

  const setContextItemId = useContextProp(
    VoyageMenuContext,
    'setContextItemId',
  );

  const getItemMenuButtonProps = useContextProp(
    VoyageMenuContext,
    'getItemMenuButtonProps',
  );

  const [isVisiblePortCostMissing] = useIsMissingPortCostVisibleState();
  const [isTCEVisible] = useVoyageBoxTCEState();
  const [isVCPVisible] = useVoyageBoxVCPState();
  const [isProfitVisible] = useVoyageBoxProfitState();
  const specialRequirements = useSpecialRequirements();

  const [isVoyageAttributeVisible] = useIsVoyageAttributeVisibleState();

  const legsOverCapacity = [...legsByOriginPortCall.values()].filter(
    (leg) => leg.overCapacityMass > 0 || leg.overCapacityVolume > 0,
  );

  const isDifferentFromIntegration =
    shipmentIntegrationStatus?.scenarioDiffersFromIntegration?.has(shipmentId);

  const legsWithUnallocatedOrders = [...legsByOriginPortCall.values()].filter(
    (leg) => leg.hasUnallocatedOrders,
  );

  const isOrderRemarksEmpty = orderRemarks.length === 0;
  const isShipmentRemarksEmpty = shipmentRemarks.length === 0;

  const bgColor =
    (combinedDiff?.hasOverlap || combinedDiff?.hasConflict) &&
    scenarioType !== 'master'
      ? COLOR_CONFLICT
      : combinedDiff?.masterUpdated ||
          combinedDiff?.masterDeleted ||
          combinedDiff?.scenarioDeleted
        ? 'orderStatus.otherUpdated'
        : scenarioType !== 'master' &&
            combinedDiff?.hasChange &&
            combinedDiff.scenarioControlled
          ? 'orderStatus.assigned'
          : isShared
            ? 'orderStatus.shared'
            : 'orderStatus.committed';

  const documentClickHandler = useEvent(() => {
    if (clickPassThrough.current) return;
    const portLocation = portCalls.map(({ portName }) => portName).join('');
    const shipmentIds = [shipmentId];
    onViewAssignment?.({
      timelineId,
      shipmentIds,
      navTarget: {
        portLocation,
        tabName: 'documents',
      },
    });
  });

  const documents = useMemo(
    () => portCalls.flatMap((pc) => pc.portCallFiles),
    [portCalls],
  );

  const clickHandlers = useSingleClickHandlers();

  return (
    <Box
      {...dropProps}
      draggable={props.isSelected}
      onDragStart={(e) => {
        if (e.ctrlKey || e.metaKey) {
          e.preventDefault();
          return;
        }
        setDragData(e, dragData);
      }}
      onDragEnd={() => {
        setDropEffect('none', '', true);
      }}
      {...clickHandlers}
      ref={(ref) => {
        elRef.current = ref;
      }}
      display="flex"
      flexDirection="column"
      height="100%"
      style={{
        ...(borderColor &&
          isVoyageAttributeVisible && {
            borderColor,
            borderWidth: '3px',
            borderStyle: 'solid',
            borderBottomWidth: 'none',
          }),
      }}
      position="relative"
    >
      <View
        flexDirection="row"
        bg={bgColor}
        overflow="hidden"
        height="20px"
        alignItems="center"
        justifyContent="space-between"
      >
        <FailedRoutesIndicator
          disableTooltip={isDragOver}
          failedRoutes={failedRouteLegs}
        />
        {portCalls.map(
          (
            {
              clientId: portCallClientId,
              canSwapLeftShipments,
              canSwapRightShipments,
              portName,
              events,
              hasCargoEvents,
              start,
              portId,
            },
            index,
          ) => {
            const hasPortCostEstimate =
              type === ItemType.SCHEDULED_ORDER &&
              (events.find(isPortCallArrivalEventTimeData)?.costs
                .portCostEstimate ?? 0) > 0;
            const isPortCostMissing =
              type === ItemType.SCHEDULED_ORDER && !hasPortCostEstimate;

            const arrival = events.find(
              (i) =>
                isPortCallEvent(i.event) &&
                i.event.event === PortCallEventType.Arrival,
            );

            const departure = events.find(
              (i) =>
                isPortCallEvent(i.event) &&
                i.event.event === PortCallEventType.Departure,
            );

            const portTimezone =
              getEventLocation(events[0].event, locations).timezone ??
              undefined;

            const eta = arrival?.start
              ? timeFieldFormat(arrival.start, portTimezone)
              : undefined;
            const etd = departure?.start
              ? timeFieldFormat(departure.start, portTimezone)
              : undefined;

            const isLoad = events.find((i) => isLoadEvent(i.event));

            const portDraftWarningStatusAtArrival = arrival
              ? getDraftWarningAtEvent(arrival)
              : undefined;
            const portDraftWarningStatusAtDeparture = departure
              ? getDraftWarningAtEvent(departure)
              : undefined;

            const orders = events
              .filter(isTCOrderEventTimeData)
              .map((i) => i.event.order);

            const vessel = events[0].event.vessel;

            const specialRequirementsWarnings = vessel
              ? getSpecialRequirementsWarning(
                  vessel,
                  orders,
                  specialRequirements,
                  { portId, portName },
                )
              : undefined;

            const portCallIndicatorTooltip = (
              <Tooltip
                disabled={isDragOver}
                content={
                  <VoyageTooltip
                    eta={eta}
                    etd={etd}
                    isPortCostMissing={
                      isVisiblePortCostMissing && isPortCostMissing
                    }
                    {...voyageTooltip}
                  />
                }
                followCursor="horizontal"
              >
                <Box
                  display="flex"
                  onMouseEnter={() => setHoveredShipmentItem(start ?? null)}
                  onMouseLeave={() => setHoveredShipmentItem(null)}
                >
                  {isVisiblePortCostMissing && isPortCostMissing ? (
                    <RedCircleIcon />
                  ) : null}
                  {portDraftWarningStatusAtArrival && (
                    <VesselDraftWarning
                      draftIssueSummary={portDraftWarningStatusAtArrival}
                      portCallEventType={PortCallEventType.Arrival}
                    />
                  )}
                  {portDraftWarningStatusAtDeparture && (
                    <VesselDraftWarning
                      draftIssueSummary={portDraftWarningStatusAtDeparture}
                      portCallEventType={PortCallEventType.Departure}
                    />
                  )}

                  {isLoad && isCreatedInVMS ? (
                    <Box
                      marginLeft={
                        isVisiblePortCostMissing && isPortCostMissing
                          ? '15px'
                          : '22px'
                      }
                      display="flex"
                    >
                      <VesselSpecialRequirementsWarning
                        violations={specialRequirementsWarnings?.violations}
                      />
                      <Text
                        isSingleLine
                        style={{
                          textDecoration:
                            hoveredShipmentItem === start
                              ? 'underline solid red 4px'
                              : 'none',
                        }}
                      >
                        {portName}
                      </Text>
                    </Box>
                  ) : (
                    <>
                      <VesselSpecialRequirementsWarning
                        violations={specialRequirementsWarnings?.violations}
                      />

                      <Text
                        isSingleLine
                        opacity={hasCargoEvents ? 1 : 0.7}
                        style={{
                          textDecoration:
                            hoveredShipmentItem === start
                              ? 'underline solid red 4px'
                              : 'none',
                        }}
                      >
                        {portName}
                      </Text>
                    </>
                  )}
                </Box>
              </Tooltip>
            );

            const followingLeg =
              portCallClientId !== null &&
              legsByOriginPortCall.get(portCallClientId);

            return (
              <Fragment key={index}>
                <PortNameIndicator>
                  {onSwapPortCall &&
                    portCallClientId &&
                    canSwapLeftShipments.includes(shipmentId) &&
                    index > 0 &&
                    !isCompressedView && (
                      <Tooltip
                        disabled={isDragOver}
                        content={
                          <Text>
                            Change port rotation order within shipment
                          </Text>
                        }
                      >
                        <Box
                          display="inline-block"
                          height={18}
                          paddingX={1}
                          top={1}
                          position="relative"
                          minWidth="auto"
                          bg={bgColor}
                          onClick={(e) => {
                            e.stopPropagation();
                            onSwapPortCall(
                              timelineId,
                              portCallClientId,
                              [shipmentId],
                              SwapDirection.Left,
                            );
                          }}
                        >
                          <ChevronLeftIcon />
                        </Box>
                      </Tooltip>
                    )}
                  {enablePortCallUpdates ? (
                    <PortCallIndicator
                      portName={portName}
                      portCallId={portCallClientId}
                      events={events}
                      locations={locations}
                    >
                      {portCallIndicatorTooltip}
                    </PortCallIndicator>
                  ) : (
                    portCallIndicatorTooltip
                  )}
                  {onSwapPortCall &&
                    portCallClientId &&
                    canSwapRightShipments.includes(shipmentId) &&
                    index < portCalls.length - 1 &&
                    !isCompressedView && (
                      <Tooltip
                        disabled={isDragOver}
                        content={
                          <Text>
                            Change port rotation order within shipment
                          </Text>
                        }
                      >
                        <Box
                          display="inline-block"
                          height={18}
                          paddingX={1}
                          top={1}
                          position="relative"
                          minWidth="auto"
                          bg={bgColor}
                          onClick={(e) => {
                            e.stopPropagation();
                            onSwapPortCall(
                              timelineId,
                              portCallClientId,
                              [shipmentId],
                              SwapDirection.Right,
                            );
                          }}
                        >
                          <ChevronRightIcon />
                        </Box>
                      </Tooltip>
                    )}
                </PortNameIndicator>

                {isLoad && isCreatedInVMS && (
                  <LockIcon data-testid="voyage-lock-icon" />
                )}

                {followingLeg && !isCompressedView && (
                  <AvailableCapacityIndicator
                    disableTooltip={isDragOver}
                    leg={followingLeg}
                  />
                )}
              </Fragment>
            );
          },
        )}
      </View>
      {(!isCompressedView || (isCompressedView && isSelected)) && (
        <Box
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          px={2}
          height="18px"
        >
          <View
            height="100%"
            width={1}
            overflow="hidden"
            flexDirection="row"
            alignItems="center"
          >
            {legsOverCapacity.length > 0 && (
              <OverCapacityIndicator
                disableTooltip={isDragOver}
                legsOverCapacity={legsOverCapacity}
              />
            )}

            {isDifferentFromIntegration && (
              <DifferentFromIntegrationIndicator
                shipmentIntegrationStatus={shipmentIntegrationStatus}
                disableTooltip={isDragOver}
                shipmentId={shipmentId}
              />
            )}
            <AssortmentExclusionsIndicator
              disableTooltip={isDragOver}
              assortmentExclusions={assortmentExclusions}
            />
            <MaxDwtViolationIndicator
              disableTooltip={isDragOver}
              portCalls={portCalls}
              vesselWeightCapacity={vesselWeightCapacity}
            />
            <DraftViolationIndicator
              disableTooltip={isDragOver}
              portCalls={portCalls}
            />
            {legsOverCapacity.length === 0 &&
              legsWithUnallocatedOrders.length > 0 && (
                <UnallocatedOrdersIndicator
                  disableTooltip={isDragOver}
                  legsWithUnallocated={legsWithUnallocatedOrders}
                />
              )}
            {onViewAssignment && documents.length ? (
              <Tooltip
                disabled={isDragOver}
                followCursor="horizontal"
                content={
                  <View>
                    <Text>
                      {documents.length} document
                      {documents.length > 1 ? 's' : null}
                    </Text>
                    {documents.map((doc) => {
                      const type = documentTypeOptions.find(
                        (i) => i.value === doc.type,
                      )?.label;
                      return (
                        <Text key={doc.id}>
                          {type ? `${type} ` : null}
                          {doc.note ? ` -> ${doc.note}` : null}
                        </Text>
                      );
                    })}
                  </View>
                }
              >
                <Box
                  width={13}
                  height={13}
                  minWidth={13}
                  display="flex"
                  alignItems="center"
                  mr="2px"
                >
                  <DocumentIcon onClick={documentClickHandler} />
                </Box>
              </Tooltip>
            ) : null}
            {(!isOrderRemarksEmpty || !isShipmentRemarksEmpty) && (
              <Tooltip
                disabled={isDragOver}
                content={
                  <View>
                    {orderRemarks.length ? (
                      <Text>
                        Order remarks:{' '}
                        {orderRemarks.length > 1
                          ? orderRemarks.join(', ')
                          : orderRemarks}
                      </Text>
                    ) : null}
                    {shipmentRemarks ? (
                      <Text>Shipment remarks: {shipmentRemarks}</Text>
                    ) : null}
                  </View>
                }
                followCursor="horizontal"
              >
                <Box
                  width={13}
                  height={13}
                  minWidth={13}
                  display="flex"
                  alignItems="center"
                  mr="2px"
                >
                  <NoteIcon />
                </Box>
              </Tooltip>
            )}
            {type === ItemType.SCHEDULED_ORDER && isTCEVisible && tceData && (
              <VoyageBoxDataContainer>
                <ShipmentField
                  content={
                    <Tooltip
                      content={
                        <TceDataTooltip
                          baseCurrency={baseCurrency ?? null}
                          tceData={tceData}
                        />
                      }
                    >
                      <span>
                        {formatCurrency(tceData.tce ?? 0, 0, baseCurrency)} /
                        day
                      </span>
                    </Tooltip>
                  }
                />
              </VoyageBoxDataContainer>
            )}
            {isVCPVisible && metrics !== undefined && (
              <VoyageBoxDataContainer>
                <ShipmentField
                  content={<span>{formatNumber(+metrics.vcp, 0)}P</span>}
                />
              </VoyageBoxDataContainer>
            )}
            {isProfitVisible && tceData && (
              <VoyageBoxDataContainer>
                <ShipmentField
                  content={
                    <Tooltip
                      content={
                        <ProfitTooltip
                          baseCurrency={baseCurrency ?? null}
                          tceData={tceData}
                        />
                      }
                    >
                      <span>
                        {formatCurrency(
                          tceData.income - tceData.totalCost,
                          0,
                          baseCurrency,
                        )}
                      </span>
                    </Tooltip>
                  }
                />
              </VoyageBoxDataContainer>
            )}
            <ShipmentCharterersField charterers={charterers} />
            <ShipmentAssortmentsField
              assortments={assortments}
              showInquiries={showInquiries}
            />
          </View>
        </Box>
      )}
      <View height="4px" width={1} bg="neutral.white_50" />
      {isDragOver && (
        <View
          position="absolute"
          top={0}
          right={0}
          bottom={0}
          left={0}
          bg="blue.blue_200"
          opacity={0.9}
          justifyContent="center"
          alignItems="center"
          style={{ pointerEvents: 'none' }}
        >
          <Text color="neutral.white_50" fontWeight="bold">
            Add to shipment
          </Text>
        </View>
      )}
      {enableVoyageCharterUpdates && voyageCharterId !== null ? (
        <Box
          position="absolute"
          top={-8}
          right={-8}
          display="flex"
          justifyContent="center"
          alignItems="center"
          zIndex={100}
        >
          <VoyageCharterUpdateIndicator voyageCharterId={voyageCharterId} />
        </Box>
      ) : null}

      {setContextItemId && getItemMenuButtonProps && (
        <Box position="absolute" top={4} right={1}>
          <ContextMenuIcon
            {...getItemMenuButtonProps({
              onPointerDown: (e) => {
                e.stopPropagation();
                e.preventDefault();
                setContextItemId(id);
              },
            })}
          />
        </Box>
      )}
    </Box>
  );
});

export const TimelineCompletedVoyageRenderer = React.memo(
  function TimelineCompletedVoyageRenderer(props: {
    item: TimelineItem<TimelineCompletedVoyage>;
  }) {
    const { item } = props;
    const { isCompressedView, portCalls } = item;
    const [isVoyageCargoVisible] = useIsVoyageCargoVisibleState();
    return (
      <Box
        width={1}
        height="100%"
        bg="neutral.white_50"
        display="flex"
        flexDirection="column"
        style={contain}
      >
        <Box
          height="20px"
          display="flex"
          overflow="hidden"
          alignItems="center"
          flexDirection="row"
          justifyContent="space-between"
          bg="orderStatus.committed"
        >
          {portCalls.map((portName, i) => (
            <PortNameIndicator key={i} portName={portName} />
          ))}
        </Box>
        {!isCompressedView && (
          <Box
            px={2}
            width={1}
            height="18px"
            overflow="hidden"
            alignItems="center"
            flexDirection="row"
            justifyContent="space-between"
          >
            {isVoyageCargoVisible ? (
              <Text
                mr={2}
                flex="none"
                color="text"
                fontSize={2}
                isSingleLine
                isUnselectable
                display="flex"
                overflow="hidden"
                alignItems="center"
              >
                {item.cargo}
              </Text>
            ) : null}
          </Box>
        )}
      </Box>
    );
  },
);
